<template>
  <f7-popup
    class="main-pc-part-warehouses-popup"
    close-on-escape
    :opened="isPopupOpened"
    @popup:closed="closePopup"
  >
    <!-- Content of the popup -->
    <f7-page v-if="isPopupOpened">
      <template #fixed>
        <TheNavbar
          nav-title="Añadir artículo"
          class="at-navbar secondary-navbar"
          :popup-close-btn-enable="true"
        />
      </template>

      <UCPreload
        :show="isLoadingWarehouses"
        :number="1"
      />

      <Transition>
        <template v-if="!isLoadingWarehouses">
          <div
            v-if="warehouses && warehouses.length > 0"
            class="margin-left margin-right"
          >
            <!-- Search warehouses -->
            <UCSearchEntityERP
              title="Búsqueda del almacén"
              :placeholder="$t('searchEntity.common.searchByCodeOrName')"
              :search-text="$t('searchEntity.warehouse.searchText')"
              :search-cancellation-text="$t('searchEntity.warehouse.searchCancellationText')"
              :search-not-found-text="$t('searchEntity.warehouse.searchNotFoundText')"
              :default-etities-list="
                warehouses"
              :default-value="warehouses.length > 1 ? undefined : warehouses[0]"
              :property-entity-shown-input="['Codigo', 'Nombre']"
              :property-entity-shown-list-title="['Codigo', 'Nombre']"
              :emit-evet-change-default-value="true"
              :autofocus-on-deselection="true"
              @selection="onWarehouseSelection"
            />

            <!-- Search articles -->
            <UCSearchEntityERP
              title="Búsqueda de artículos"
              :placeholder="$t('searchEntity.article.placeholder.searchByNameOrCode')"
              :search-text="$t('searchEntity.article.searchText')"
              :search-cancellation-text="$t('searchEntity.article.searchCancellationText')"
              :search-not-found-text="$t('searchEntity.article.searchNotFoundText')"
              :end-point-for-code-search="fetchArticles"
              :end-point-for-list="fetchArticles"
              :property-entity-shown-input="['Articulo.Codigo', 'Articulo.Nombre']"
              :property-entity-shown-list-title="['Articulo.Codigo']"
              :property-entity-shown-list-subtitle="['Articulo.Nombre', 'Cantidad']"
              :autofocus-on-deselection="true"
              :multi-selection="true"
              :disabled="!selectedWarehouse"
              @selection="onArticleSelection"
              @selected-elements="onArticlesSelectedElements"
            >
              <template #subtitle-1="{ item }">
                <span class="part-warehouses-stock">{{ `${$t('common.stocks')}: ${item}` }}</span>
              </template>
            </UCSearchEntityERP>

            <template v-if="selectedArticles && selectedArticles.length > 0">
              <hr class="margin-bottom">

              <f7-block-title class="margin-left">
                Artículos seleccionados ({{ selectedArticles.length }})
              </f7-block-title>

              <!-- Articles Table -->
              <div class="container-table">
                <table>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        class="text-align-left"
                      >
                        {{ $t('common.article') }}
                      </th>
                      <th scope="col">
                        {{ $t('common.quantity') }}
                      </th>
                      <th
                        scope="col"
                        m
                      />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in selectedArticles"
                      :key="index"
                    >
                      <template v-if="item.Articulo">
                        <td class="text-align-left">
                          {{ `${item.Articulo.Codigo}/${item.Articulo.Nombre}` }}
                        </td>
                        <td>
                          <!-- This container is used to center the stepper correctly -->
                          <div class="display-flex justify-content-center">
                            <f7-stepper
                              fill
                              :manual-input-mode="true"
                              :min="10 ** -(item.Articulo.PrecisionCantidades)"
                              :max="item.Cantidad"
                              :value="Number(item.CantidadSeleccionadaInicial)"
                              :decimal-point="item.Articulo.PrecisionCantidades"
                              :step="10 ** -(item.Articulo.PrecisionCantidades)"
                              :format-value="(/** @type {string} */ value) => formatInputValue(item, value)"
                              @stepper:change="item.CantidadSeleccionada = $event"
                            />
                          </div>
                        </td>
                        <td>
                          <f7-button @click="onDeleteArticleClick(item, index)">
                            <UCSvgIcon
                              name="fa6-solid-trash"
                              color="var(--his-tomate)"
                            />
                          </f7-button>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Confirm button -->
              <div class="margin-top margin-bottom">
                <f7-button
                  fill
                  color="green"
                  @click="onWarehouseArticlesConfirm"
                >
                  Añadir selección
                </f7-button>
              </div>
            </template>
          </div>

          <div
            v-else
            class="container-no-warehouses"
          >
            No hay almacenes
          </div>
        </template>
      </Transition>
    </f7-page>
  </f7-popup>
</template>

<script setup>
// @ts-check
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppConfigStore } from '@/js/stores/appConfig';
import { ConfirmDialogGH, HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import {
  callFetchAccumulatedStock,
  callFetchWarehouses,
  callSaveArticles,
} from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';

import TheNavbar from '@/components/TheNavbar.vue';
import UCSearchEntityERP from '@/components/common/UCSearchEntityERP.vue';
import UCPreload from '@/components/common/UCPreload.vue';
import UCSvgIcon from '@/components/common/UCSvgIcon.vue';

/** @import { Ref } from 'vue' */
/** @import { WorkPartItem, WorkPartDetails } from '@/js/stores/repairParts' */
/**
 * @import {
 * AcumuladoExistencias,
 * Almacen,
 * Articulo,
 * DatosParteReparacion,
 * ParteProduccionRealEntrada,
 * } from '@erpagroapp/types' */
/**
 * @typedef {AcumuladoExistencias & {
 * CantidadSeleccionada: string
 * CantidadSeleccionadaInicial: string
 * }} AcumuladoExistenciasCantidadSeleccionada
 */

const emit = defineEmits(['added']);

const isPopupOpened = ref(false);
const isLoadingWarehouses = ref(false);

/**
 * Stores the selected work part
 * @type {Ref<WorkPartItem | WorkPartDetails | null>}
 */
const selectedWorkPart = ref(null);

/**
 * Stores an array of warehouses (as `Almacen` objects)
 * @type {Ref<Almacen[]>}
 */
const warehouses = ref([]);

/**
 * Stores the selected warehouse (as `Almacen` object)
 * @type {Ref<Almacen | null>}
 */
const selectedWarehouse = ref(null);

/**
 * Stores a list of articles for a selected warehouse (as `Article` objects)
 * @type {Ref<Articulo[]>}
 */
const accumulatedStock = ref([]);

/**
 * Stores a list of selected articles (as `AcumuladoExistenciasCantidadSeleccionada` objects)
 * @type {Ref<AcumuladoExistenciasCantidadSeleccionada[]>}
 */
const selectedArticles = ref([]);

const { userEnviroment } = storeToRefs(useAppConfigStore());

async function fetchWorkPartWarehouses() {
  if (!selectedWorkPart.value) {
    return false;
  }

  let successful = false;
  try {
    isLoadingWarehouses.value = true;
    const { data } = await callFetchWarehouses(selectedWorkPart.value.Id_ParteProduccion);
    isLoadingWarehouses.value = false;

    if (Array.isArray(data)) {
      warehouses.value = data;
      selectedArticles.value = [];
      successful = true;
    } else {
      HiNotification.Toast('No se han podido obtener los almacenes');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se han podido obtener los almacenes');
  } finally {
    isLoadingWarehouses.value = false;
  }

  return successful;
}

/**
 * A method that is called to open the popup.
 * @param {WorkPartItem | WorkPartDetails} workPart - The work part.
 */
async function openPopup(workPart) {
  if (!workPart) {
    throw new Error('No work part provided');
  }

  selectedWorkPart.value = workPart;

  let successful = false;

  try {
    isPopupOpened.value = true;
    successful = await fetchWorkPartWarehouses();
  } finally {
    if (!successful) {
      isPopupOpened.value = false;
    }
  }
}

/** A method that is used to clear data when the popup is closed */
function clearData() {
  selectedWorkPart.value = null;
  warehouses.value = [];
  selectedWarehouse.value = null;
  accumulatedStock.value = [];
  selectedArticles.value = [];
}

/** A method that is called when popup closes */
function closePopup(saved = false) {
  if (isPopupOpened.value) {
    isPopupOpened.value = false;

    if (saved) {
      emit('added', selectedWorkPart.value);
    }

    clearData();
  }
}

/**
 * A method that is called when user selects a part warehouse in the popup
 * @param {Almacen | null} value - The selected warehouse.
 */
function onWarehouseSelection(value) {
  selectedWarehouse.value = value;
  if (!value) {
    accumulatedStock.value = [];
  }
}

/**
 * A method that is called when user searches by value
 * @param {string | null} value - The search value.
 */
async function fetchArticles(value) {
  if (!userEnviroment.value) {
    return null;
  }

  try {
    return await callFetchAccumulatedStock({
      filter: value ? `q:${value}` : '',
      warehouseId: selectedWarehouse.value?.Id,
      enterpriseGroupId: userEnviroment.value.EntornoDeTrabajo.GrupoEmpresarial.Id,
    });
  } catch (error) {
    logError(error);
    return null;
  }
}

/**
 * A method that is called when user confirms the articles selection in the popup.
 * @param {AcumuladoExistenciasCantidadSeleccionada[]} selectedItems - The selected articles.
 */
function onArticlesSelectedElements(selectedItems) {
  if (!selectedArticles.value) {
    return;
  }

  if (selectedItems.length > 0) {
    const existingItemCodes = new Set(selectedArticles.value.map((item) => item.Articulo?.Id));

    // Filter selectedItems to find articles that don't exist in the Set,
    // and create a new array with objects having an additional property (CantidadSeleccionada)
    const newItems = selectedItems
      .filter((item) => !existingItemCodes.has(item.Articulo?.Id))
      .map((item) => {
        const initialQuantity = item.Cantidad < 1 ? item.Cantidad.toString() : '1';

        return {
          ...item,
          CantidadSeleccionada: initialQuantity,
          CantidadSeleccionadaInicial: initialQuantity,
        };
      });

    selectedArticles.value.push(...newItems);
  } else {
    selectedArticles.value = [];
  }
}

/**
 * A method that is called when user selects an article
 * @param {AcumuladoExistenciasCantidadSeleccionada | null} value - The selected article.
 */
function onArticleSelection(value) {
  if (value != null) {
    onArticlesSelectedElements([value]);
  }
}

/**
 * A method that is called when user clicked on the 'Delete Article' button
 * @param {AcumuladoExistenciasCantidadSeleccionada} article - The article to delete.
 * @param {number} i - The index of the article in the list.
 */
async function onDeleteArticleClick(article, i) {
  if (!selectedArticles.value || !article.Articulo) {
    return;
  }

  const resultConfirm = await ConfirmDialogGH(
    `Esta acción eliminará el artículo <strong>${article.Articulo.Codigo}/${article.Articulo.Nombre}</strong>.`,
    '¿Quiere continuar?',
  );

  if (resultConfirm) {
    selectedArticles.value.splice(i, 1);
  }
}

/**
 * A method that adds multiple articles to the part
 * @param {PartialDeep<DatosParteReparacion>} repairPartData - The repair part data.
 */
async function saveArticles(repairPartData) {
  HiDialog.Preloader(true, 'Añadiendo artículos al parte...');

  try {
    const { data } = await callSaveArticles(repairPartData);

    if (data.Valor === true) {
      const articles = repairPartData.ParteProduccion?.ParteProduccionReal?.DatosEntrada?.length ?? 0;
      if (articles > 1) {
        HiNotification.Toast('Los artículos se han añadido correctamente al parte');
      } else {
        HiNotification.Toast('El artículo se ha añadido correctamente al parte');
      }

      closePopup(true);
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido añadir artículos al parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido añadir artículos al parte');
  } finally {
    HiDialog.Preloader(false);
  }
}

/** A method that is called when user confirms the selected articles to be added to the part */
function onWarehouseArticlesConfirm() {
  if (!selectedWorkPart.value || !selectedArticles.value) {
    return;
  }

  const selected = selectedArticles.value
    .filter((article) => Number(article.CantidadSeleccionada) > 0);

  if (selected.length === 0) {
    return;
  }

  /** @type {PartialDeep<ParteProduccionRealEntrada>[]} */
  const realEntries = selected.map((item) => ({
    Id: 0,
    Almacen: {
      Id: selectedWarehouse.value?.Id,
    },
    Articulo: item.Articulo,
    Cantidad: Number(item.CantidadSeleccionada),
    Observaciones: '',
    Precio: 0.0,
  }));

  /** @type {PartialDeep<DatosParteReparacion>} */
  const repairPartData = {
    ParteProduccion: {
      Id: selectedWorkPart.value.Id_ParteProduccion,
      ParteProduccionReal: {
        Id: 0,
        DatosEntrada: realEntries,
      },
    },
  };

  saveArticles(repairPartData);
}

/**
 * Updates the selected amount of an article.
 * @param {AcumuladoExistenciasCantidadSeleccionada} item - The item to update.
 * @param {string} value - The current value.
 * @returns {string} The new value.
 */
function formatInputValue(item, value) {
  const precision = item.Articulo?.PrecisionCantidades ?? 0;
  if (value === '') {
    return '';
  }

  const numericValue = Number(value);
  return Number.isInteger(numericValue) ? numericValue.toString() : numericValue.toFixed(precision);
}

defineExpose({
  openPopup,
  closePopup,
});
</script>

<style scoped lang="scss">
.main-pc-part-warehouses-popup {
  .container-table {
    overflow-x: auto;

    table {
      width: 100%;
      border-collapse: collapse;
      border-spacing: 0;

      thead {
        background-color: #9e9e9e;

        th {
          background-color: #9e9e9e;
          padding: 5px;
        }

        th:first-child {
          border-top-left-radius: 5px;
        }

        th:last-child {
          border-top-right-radius: 5px;
        }
      }

      tbody {
        background-color: #e6e6e6;

        td {
          padding: 5px;
        }

        td:first-child {
          word-break: break-all;
        }
      }
    }
  }

  .container-no-warehouses {
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(100% - var(--f7-page-navbar-offset));
    margin: 0;
  }

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
}

.Search-popup {
  #search-entity-page-content {
    .part-warehouses-stock {
      font-size: 12px;
    }
  }
}
</style>
